import { useEffect, useRef } from "react";

import { useLocation } from "@remix-run/react";

import { trackEvent } from "~/lib/analytics";

// AIDEV-NOTE: Centralized screen view tracking for SSR + client routing
// Emits "viewed screen" on initial mount and every pathname change.
// Relies on trackEvent auto-enrichment for session/ownership/environment.
export function AnalyticsScreenTracker() {
    const location = useLocation();
    const prevScreenRef = useRef<string | undefined>(undefined);
    const lastTrackedRef = useRef<{ screen: string; timestamp: number } | null>(
        null,
    );

    useEffect(() => {
        const screen = location.pathname || "/";
        const lastScreen = prevScreenRef.current;
        const now = Date.now();

        // AIDEV-NOTE: Prevent duplicate tracking from React StrictMode double-firing useEffect
        // Skip if we just tracked this exact screen within the last 100ms
        if (
            lastTrackedRef.current &&
            lastTrackedRef.current.screen === screen &&
            now - lastTrackedRef.current.timestamp < 100
        ) {
            return;
        }

        // Fire page/screen view event
        trackEvent("viewed screen", {
            screen,
            lastScreen,
        });

        // Update refs for next navigation
        prevScreenRef.current = screen;
        lastTrackedRef.current = { screen, timestamp: now };
        // We intentionally only depend on pathname so search/hash changes don't spam
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return null;
}
